import React from 'react';
import { FileText, Download, Image as ImageIcon, Video, Music } from 'lucide-react';
import { Button } from '../../../components/ui';

interface MessageMediaProps {
  type: string;
  mediaUrl: string;
  caption?: string;
  filename?: string;
  direction: 'INBOUND' | 'OUTBOUND';
}

export const MessageMedia: React.FC<MessageMediaProps> = ({
  type,
  mediaUrl,
  caption,
  filename,
  direction,
}) => {
  const handleDownload = () => {
    window.open(mediaUrl, '_blank');
  };

  const getFileName = () => {
    if (filename) return filename;
    const urlParts = mediaUrl.split('/');
    return urlParts[urlParts.length - 1] || 'file';
  };

  const getFileExtension = () => {
    const name = getFileName();
    const parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
  };

  // IMAGE
  if (type === 'IMAGE') {
    return (
      <div className="max-w-xs">
        <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
          <img
            src={mediaUrl}
            alt={caption || 'Imagem'}
            className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
            onClick={handleDownload}
            loading="lazy"
          />
        </div>
        {caption && (
          <p className={`text-sm mt-2 ${direction === 'OUTBOUND' ? 'text-white' : 'text-foreground'}`}>
            {caption}
          </p>
        )}
      </div>
    );
  }

  // DOCUMENT
  if (type === 'DOCUMENT') {
    return (
      <div className="min-w-[280px] max-w-sm">
        <div
          className={`rounded-lg p-4 border ${
            direction === 'OUTBOUND'
              ? 'bg-white/10 border-white/20'
              : 'bg-white dark:bg-gray-800 border-border'
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`p-3 rounded-lg ${
                direction === 'OUTBOUND' ? 'bg-white/20' : 'bg-blue-50 dark:bg-blue-900/20'
              }`}
            >
              <FileText
                className={`h-6 w-6 ${
                  direction === 'OUTBOUND' ? 'text-white' : 'text-blue-500'
                }`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium truncate ${
                  direction === 'OUTBOUND' ? 'text-white' : 'text-foreground'
                }`}
              >
                {getFileName()}
              </p>
              <p
                className={`text-xs mt-1 ${
                  direction === 'OUTBOUND' ? 'text-white/70' : 'text-muted-foreground'
                }`}
              >
                {getFileExtension()}
              </p>
            </div>
            <Button
              size="sm"
              variant={direction === 'OUTBOUND' ? 'ghost' : 'outline'}
              onClick={handleDownload}
              className={`flex-shrink-0 ${direction === 'OUTBOUND' ? 'text-white hover:bg-white/10' : ''}`}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {caption && (
          <p className={`text-sm mt-2 ${direction === 'OUTBOUND' ? 'text-white' : 'text-foreground'}`}>
            {caption}
          </p>
        )}
      </div>
    );
  }

  // VIDEO
  if (type === 'VIDEO') {
    return (
      <div className="max-w-sm">
        <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
          <video
            controls
            className="w-full h-auto"
            preload="metadata"
          >
            <source src={mediaUrl} type="video/mp4" />
            Seu navegador não suporta reprodução de vídeo.
          </video>
        </div>
        {caption && (
          <p className={`text-sm mt-2 ${direction === 'OUTBOUND' ? 'text-white' : 'text-foreground'}`}>
            {caption}
          </p>
        )}
      </div>
    );
  }

  // AUDIO
  if (type === 'AUDIO') {
    return (
      <div className="min-w-[280px] max-w-sm">
        <div
          className={`rounded-lg p-4 border ${
            direction === 'OUTBOUND'
              ? 'bg-white/10 border-white/20'
              : 'bg-white dark:bg-gray-800 border-border'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-3 rounded-full ${
                direction === 'OUTBOUND' ? 'bg-white/20' : 'bg-green-50 dark:bg-green-900/20'
              }`}
            >
              <Music
                className={`h-5 w-5 ${
                  direction === 'OUTBOUND' ? 'text-white' : 'text-green-500'
                }`}
              />
            </div>
            <div className="flex-1">
              <audio controls className="w-full">
                <source src={mediaUrl} type="audio/mpeg" />
                Seu navegador não suporta reprodução de áudio.
              </audio>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback para outros tipos
  return (
    <div className="flex items-center gap-2">
      <a
        href={mediaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline text-sm ${direction === 'OUTBOUND' ? 'text-white' : 'text-blue-500'}`}
      >
        Download {type}
      </a>
    </div>
  );
};
